// src/components/auth/AuthContext.tsx
//
// React context for authentication state.
//
// Provides:
//   - accountType: AccountType (anonymous | demo | admin | customer)
//   - username: string | undefined
//   - email: string | undefined
//   - groups: string[]
//   - isAuthenticated: boolean
//   - isReady: boolean (true after Keycloak init completes)
//   - token: string | undefined (for API requests)
//   - login(): trigger Keycloak login flow
//   - logout(): trigger Keycloak logout flow

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { initKeycloak, getKeycloak, login as keycloakLogin, logout as keycloakLogout } from "@/lib/keycloak";
import { BFF_ENABLED, fetchBffSession, bffLogin, bffLogout } from "@/lib/bffAuth";
import { getAccountType, type AccountType } from "@/lib/auth";
import i18n from "@/i18n";
import {
  pullLanguagePreference,
  pushLanguagePreference,
} from "@/lib/langPreference";

export interface AuthUser {
  username?: string;
  sub?: string;
  issuer?: string;
  email?: string;
  fullName?: string;
}

export interface AuthContextValue {
  isReady: boolean;
  isAuthenticated: boolean;
  accountType: AccountType;
  user: AuthUser | undefined;
  groups: string[];
  token: string | undefined;
  login: (next?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider wraps the app, initializes Keycloak on mount,
 * and provides the auth state via context to all children.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accountType, setAccountType] = useState<AccountType>("anonymous");
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [groups, setGroups] = useState<string[]>([]);
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    // BFF mode (default): no browser-side tokens at all. The session
    // cookie is HttpOnly; we only learn the profile + CSRF token.
    if (BFF_ENABLED) {
      fetchBffSession().then((s) => {
        if (cancelled) return;
        setIsReady(true);
        if (s) {
          setIsAuthenticated(true);
          setUser({ username: s.username, sub: s.sub ?? "", issuer: s.issuer ?? "" });
          setGroups(s.groups);
          setAccountType(getAccountType(s.groups));
          // The account's stored language wins over this browser's
          // localStorage copy (the choice follows the account).
          signedIn = true;
          void pullLanguagePreference();
        }
      });
      // Keep the account preference in sync with the switcher —
      // only when a session actually exists (anonymous switches stay
      // local, and never hit the cookie-only endpoint).
      let signedIn = false;
      const onLanguageChanged = (lng: string) => {
        if (!signedIn) return;
        void pushLanguagePreference(lng.toLowerCase().split(/[-_]/)[0] ?? "");
      };
      i18n.on("languageChanged", onLanguageChanged);
      return () => {
        cancelled = true;
        i18n.off("languageChanged", onLanguageChanged);
      };
    }

    const refresh = (kc: import("keycloak-js").default) => {
      if (cancelled) return;
      setIsReady(true);
      setIsAuthenticated(kc.authenticated ?? false);
      setToken(kc.token);
      if (kc.authenticated && kc.tokenParsed) {
        const parsed = kc.tokenParsed as {
          sub?: string;
          iss?: string;
          preferred_username?: string;
          email?: string;
          name?: string;
          groups?: string[];
        };
        setUser({
          username: parsed.preferred_username ?? "",
          sub: parsed.sub ?? "",
          issuer: parsed.iss ?? "",
          email: parsed.email ?? "",
          fullName: parsed.name ?? "",
        });
        setGroups(parsed.groups ?? []);
        setAccountType(getAccountType(parsed.groups ?? []));
      } else {
        setUser(undefined);
        setGroups([]);
        setAccountType("anonymous");
      }
    };

    initKeycloak().then(refresh);

    // Hook into Keycloak's events so refresh-on-login works
    // when the user is redirected back from the SSO provider
    const kc = getKeycloak();
    if (kc) {
      kc.onAuthSuccess = () => refresh(kc);
      kc.onAuthLogout = () => refresh(kc);
      kc.onAuthRefreshSuccess = () => refresh(kc);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const login = (next?: string) =>
    (BFF_ENABLED ? bffLogin(next) : keycloakLogin());
  const logout = () => (BFF_ENABLED ? void bffLogout() : keycloakLogout());

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        accountType,
        user,
        groups,
        token,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * useAuth hook. Returns the AuthContext value.
 * Throws if used outside an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an <AuthProvider />");
  }
  return ctx;
}

// ----- internal helper -----

// AccountType -> getAccountType is now in src/lib/auth.ts (single source of truth).

// Useful for tests / debugging
export function _getKeycloak() {
  return getKeycloak();
}
